import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { FlightSearchSelectors } from "@b2bportal/core-flights";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import dayjs from "dayjs";

interface UseFlightShopSearchResponse {
  context: {
    openMobileSearch: boolean;
    text: string;
    subtext: string;
  };
  handlers: {
    onClose: () => void;
    handleOnSearch: () => void;
    onSearchClick: () => void;
  };
}

export const useFlightShopSearch = (
  routeIndex = 0
): UseFlightShopSearchResponse => {
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const [openMobileSearch, setOpenMobileSearch] = useState<boolean>(false);

  const onClose = useCallback(() => {
    setOpenMobileSearch(false);
  }, []);

  const search = useSelector(FlightSearchSelectors.getSearchFormValues);

  const handleOnSearch = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSearchClick = useCallback(() => setOpenMobileSearch(true), []);

  const isMulticityRoute =
    search.tripCategory === TripCategory.MULTI_CITY &&
    search.multicityRoutes[routeIndex];

  const multicityRouteText = `${search.multicityRoutes[routeIndex]?.origin?.label} - ${search.multicityRoutes[routeIndex]?.destination?.label}`;

  const subtext = isMulticityRoute
    ? multicityRouteText
    : `${search.origin?.label} - ${search.destination?.label}`;

  const departureDate = isMulticityRoute
    ? search.multicityRoutes[routeIndex]?.departureDate
    : search.departureDate;

  const formattedDepartureDate = formatDateTime(
    dayjs(departureDate).toDate(),
    DateTimeFormatStyle.Custom({ month: "short", day: "numeric" })
  );

  const formattedReturnDate =
    search.tripCategory === TripCategory.ROUND_TRIP
      ? formatDateTime(
          dayjs(search.returnDate).toDate(),
          DateTimeFormatStyle.Custom({ month: "short", day: "numeric" })
        )
      : undefined;

  // If return date is not set, only show the departure date
  const dates = formattedReturnDate
    ? `${formattedDepartureDate} - ${formattedReturnDate}`
    : formattedDepartureDate;

  const travelerCount = t("searchFilter.pickerTraveler", {
    count:
      search.passengerCounts.adultsCount + search.passengerCounts.childrenCount,
  });

  const category =
    search.tripCategory === TripCategory.ONE_WAY
      ? t("oneWay")
      : search.tripCategory === TripCategory.MULTI_CITY
        ? t("flightMultiCity")
        : t("roundTrip");

  const text = `${dates}, ${travelerCount}, ${category}`;

  return {
    context: {
      openMobileSearch,
      subtext,
      text,
    },
    handlers: {
      onClose,
      handleOnSearch,
      onSearchClick,
    },
  };
};
